import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const LEAVE_COLOR = {
  ANNUAL: "bg-[#0047FF]",
  SICK: "bg-[#FF3333]",
  UNPAID: "bg-black",
  WFH: "bg-[#FF66B2]",
  OVERTIME: "bg-[#FF8000]",
  TIME_CORRECTION: "bg-[#FFCC00]",
};

function fmtMinutes(mins) {
  const m = Math.round(mins);
  const sign = m < 0 ? "-" : "";
  const abs = Math.abs(m);
  const h = Math.floor(abs / 60);
  const mm = abs % 60;
  return `${sign}${h}:${String(mm).padStart(2, "0")}`;
}

export default function MyStatus() {
  const { t } = useI18n();
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/me/summary", { params: { year, month } }),
        api.get("/attendance/events", { params: { user_id: user.id } }),
      ]);
      setSummary(s.data);
      setEvents(e.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [year, month]);

  const maxMinutes = useMemo(() => {
    if (!summary) return 8 * 60;
    return Math.max(
      8 * 60,
      ...summary.days.map((d) => Math.max(d.work_minutes + d.wfh_minutes, d.obligation_minutes))
    );
  }, [summary]);

  const eventMix = summary?.event_counts || {};
  const eventMixTotal = Object.values(eventMix).reduce((a, b) => a + b, 0);

  const months = [...Array(12)].map((_, i) => i + 1);
  const years = [year - 1, year, year + 1];

  const totalBalance = (user?.vacation_balance_days ?? 0) + (user?.vacation_balance_prior_year ?? 0);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("my_status")}</div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none" data-testid="my-status-title">
            {user?.name}
          </h1>
          <div className="text-xs font-mono text-black/60 mt-1">{user?.team || "—"}</div>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("month")}</label>
            <select data-testid="my-status-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="brut-border p-2 bg-white">
              {months.map((m) => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString(undefined, { month: "long" })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("year")}</label>
            <select data-testid="my-status-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="brut-border p-2 bg-white">
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          label={t("hour_balance_month")}
          value={summary ? fmtMinutes(summary.month_totals.saldo_minutes) : "—"}
          sub={summary ? `${t("obligation")}: ${fmtMinutes(summary.month_totals.obligation_minutes)}` : ""}
          tone={summary && summary.month_totals.saldo_minutes < 0 ? "bad" : "good"}
          testid="kpi-saldo-month"
        />
        <Kpi
          label={t("hour_balance_ytd")}
          value={summary ? fmtMinutes(summary.ytd_totals.saldo_minutes) : "—"}
          sub={summary ? `${t("worked")}: ${fmtMinutes(summary.ytd_totals.worked_minutes + summary.ytd_totals.wfh_minutes)}` : ""}
          tone={summary && summary.ytd_totals.saldo_minutes < 0 ? "bad" : "good"}
          testid="kpi-saldo-ytd"
        />
        <Kpi
          label={t("vacation_current_year")}
          value={`${user?.vacation_balance_days ?? 0}`}
          sub={t("days").toLowerCase()}
          bg="bg-[#FFCC00]"
          testid="kpi-vac-current"
        />
        <Kpi
          label={t("vacation_prior_year")}
          value={`${user?.vacation_balance_prior_year ?? 0}`}
          sub={`${t("total_balance")}: ${totalBalance}`}
          bg="bg-white"
          testid="kpi-vac-prior"
        />
      </div>

      {/* Obligation vs Worked bar */}
      {summary && (
        <div className="mt-6 brut-border bg-white p-5 brut-shadow" data-testid="obligation-bar">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-black text-xl tracking-tighter">{t("obligation_vs_worked")}</div>
            <div className="text-xs font-mono text-black/60">{new Date(year, month - 1).toLocaleString(undefined, { month: "long", year: "numeric" })}</div>
          </div>
          <StackedBar label={t("obligation")} value={summary.month_totals.obligation_minutes} max={Math.max(summary.month_totals.obligation_minutes, summary.month_totals.worked_minutes + summary.month_totals.wfh_minutes)} color="bg-black" />
          <StackedBar label={t("worked")} value={summary.month_totals.worked_minutes} secondary={summary.month_totals.wfh_minutes} max={Math.max(summary.month_totals.obligation_minutes, summary.month_totals.worked_minutes + summary.month_totals.wfh_minutes)} color="bg-[#00CC33]" secondaryColor="bg-[#FF66B2]" />
          <div className="mt-2 text-xs font-mono text-black/60 flex gap-4">
            <span><span className="inline-block w-3 h-3 bg-[#00CC33] brut-border align-middle mr-1" /> {t("work_hours")}</span>
            <span><span className="inline-block w-3 h-3 bg-[#FF66B2] brut-border align-middle mr-1" /> WFH</span>
            <span><span className="inline-block w-3 h-3 bg-black brut-border align-middle mr-1" /> {t("obligation")}</span>
          </div>
        </div>
      )}

      {/* Day-by-day */}
      {summary && (
        <div className="mt-6 brut-border bg-white p-5 brut-shadow" data-testid="daily-breakdown">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-black text-xl tracking-tighter">{t("daily_breakdown")}</div>
          </div>
          <div className="space-y-1">
            {summary.days.map((d) => {
              const total = d.work_minutes + d.wfh_minutes;
              const w = (d.work_minutes / maxMinutes) * 100;
              const f = (d.wfh_minutes / maxMinutes) * 100;
              const o = (d.obligation_minutes / maxMinutes) * 100;
              const dayObj = new Date(d.date);
              const day = dayObj.getDate();
              const weekdayName = dayObj.toLocaleDateString(undefined, { weekday: "short" });
              const rowBg = d.is_holiday ? "bg-[#FFCC00]/30" : d.is_weekend ? "bg-[#F4F4F0]" : "";
              return (
                <div key={d.date} className={`flex items-center gap-3 py-1 ${rowBg} px-2`} data-testid={`day-${d.date}`}>
                  <div className="w-16 font-mono text-xs tabular-nums">
                    <span className={d.is_weekend || d.is_holiday ? "text-[#FF3333] font-bold" : ""}>{weekdayName}</span> <span className="font-bold">{day}.{month}.</span>
                  </div>
                  <div className="flex-1 h-5 bg-[#F4F4F0] brut-border relative">
                    {d.obligation_minutes > 0 && (
                      <div className="absolute top-0 h-full border-r-2 border-dashed border-black" style={{ left: `${o}%` }} />
                    )}
                    <div className="absolute top-0 h-full bg-[#00CC33]" style={{ width: `${w}%` }} />
                    <div className="absolute top-0 h-full bg-[#FF66B2]" style={{ width: `${f}%`, left: `${w}%` }} />
                    {d.leave_type && (
                      <div className={`absolute top-0 h-full right-0 ${LEAVE_COLOR[d.leave_type] || "bg-black"} w-full opacity-70 flex items-center px-2 text-[10px] font-bold uppercase text-white`}>
                        {d.leave_type}
                      </div>
                    )}
                    {d.is_holiday && !d.leave_type && (
                      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold uppercase tracking-wider">
                        {d.holiday_name}
                      </div>
                    )}
                  </div>
                  <div className="w-24 font-mono text-xs text-right tabular-nums">
                    {total > 0 ? fmtMinutes(total) : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event mix */}
      {eventMixTotal > 0 && (
        <div className="mt-6 brut-border bg-white p-5 brut-shadow" data-testid="event-mix">
          <div className="font-display font-black text-xl tracking-tighter mb-3">{t("event_mix")}</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(eventMix)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <span key={k} className="tag bg-white">
                  {k.replace(/_/g, " ")} · <span className="font-mono">{v}</span>
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Recent events */}
      <div className="mt-6 brut-border bg-white brut-shadow overflow-hidden">
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tighter">{t("attendance")}</div>
          <div className="text-xs font-mono text-black/60">{events.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F0] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3 border-b-2 border-black">{t("time")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("type")}</th>
                <th className="text-left p-3 border-b-2 border-black">Source</th>
              </tr>
            </thead>
            <tbody data-testid="my-events-table">
              {events.slice(0, 50).map((e) => (
                <tr key={e.id} className="border-b border-black/10">
                  <td className="p-3 font-mono text-xs">{new Date(e.timestamp).toLocaleString()}</td>
                  <td className="p-3"><span className="tag bg-[#FFCC00]">{e.event_type.replace(/_/g, " ")}</span></td>
                  <td className="p-3 font-mono text-xs uppercase">{e.source}</td>
                </tr>
              ))}
              {!loading && events.length === 0 && (
                <tr><td colSpan={3} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, bg = "bg-white", tone = "neutral", testid }) {
  const toneBg = tone === "bad" ? "bg-[#FF3333] text-white" : tone === "good" ? "bg-white" : bg;
  return (
    <div className={`brut-border ${toneBg} p-4 brut-shadow`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-80">{label}</div>
      <div className="font-display font-black text-3xl tracking-tighter mt-1">{value}</div>
      {sub && <div className="text-[10px] font-mono opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function StackedBar({ label, value, secondary = 0, max, color = "bg-black", secondaryColor = "bg-[#FF66B2]" }) {
  const primaryW = (value / max) * 100;
  const secondaryW = (secondary / max) * 100;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs font-bold uppercase tracking-wider mb-1">
        <span>{label}</span>
        <span className="font-mono">{fmtMinutes(value + secondary)}</span>
      </div>
      <div className="h-6 bg-[#F4F4F0] brut-border relative overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${primaryW}%` }} />
        {secondary > 0 && <div className={`absolute top-0 h-full ${secondaryColor}`} style={{ left: `${primaryW}%`, width: `${secondaryW}%` }} />}
      </div>
    </div>
  );
}
